import * as React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { alpha, Theme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StatusTooltip } from './StatusTooltip';

export type RemarkLevel = 'error' | 'warning';

export interface RemarkItem {
  level: RemarkLevel;
  message: string;
  details?: string[];
}

type RemarkPopoverState = {
  open: boolean;
  remark: RemarkItem[];
  anchorPosition: { top: number; left: number } | null;
};

const createRemarkPopoverStore = () => {
  let state: RemarkPopoverState = {
    open: false,
    remark: [],
    anchorPosition: null,
  };
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((fn) => fn());

  return {
    getState: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    open: (remark: RemarkItem[], anchorEl: HTMLElement | SVGSVGElement) => {
      const rect = anchorEl.getBoundingClientRect();
      state = {
        open: true,
        remark,
        anchorPosition: {
          top: rect.bottom + 4,
          left: rect.left + rect.width / 2,
        },
      };
      emit();
    },
    close: () => {
      state = { open: false, remark: [], anchorPosition: null };
      emit();
    },
  };
};

const remarkPopoverStore = createRemarkPopoverStore();

function useRemarkPopoverState() {
  return React.useSyncExternalStore(
    remarkPopoverStore.subscribe,
    remarkPopoverStore.getState
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

export const LevelIcon = ({
  level,
  size = 18,
}: {
  level: RemarkLevel;
  size?: number;
}) =>
  level === 'error' ? (
    <ErrorIcon
      sx={{ fontSize: size, flexShrink: 0, color: `${level}.main` }}
    />
  ) : (
    <WarningAmberIcon
      sx={{ fontSize: size, flexShrink: 0, color: `${level}.main` }}
    />
  );

const RemarkDetailRow = ({
  detail,
  level,
}: {
  detail: string;
  level: RemarkLevel;
}) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      bgcolor: alpha(theme.palette[level].main, 0.06),
      borderLeft: `3px solid ${theme.palette[level].main}`,
      fontSize: '0.8rem',
      lineHeight: 1.45,
      color: 'text.secondary',
    })}
  >
    {detail}
  </Box>
);

export const RemarkCard = ({ remark }: { remark: RemarkItem }) => {
  const level: RemarkLevel = remark.level === 'error' ? 'error' : 'warning';

  return (
    <Box
      sx={(theme: Theme) => ({
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette[level].main, 0.3)}`,
        bgcolor: alpha(theme.palette[level].main, 0.04),
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette[level].main, 0.08),
          borderBottom: remark.details?.length
            ? `1px solid ${alpha(theme.palette[level].main, 0.15)}`
            : 'none',
        })}
      >
        <LevelIcon level={level} />
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            lineHeight: 1.4,
            color: `${level}.dark`,
          }}
        >
          {remark.message}
        </Box>
      </Box>

      {remark.details && remark.details.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.5,
          }}
        >
          {remark.details.map((detail, dIdx) => (
            <RemarkDetailRow key={dIdx} detail={detail} level={level} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// RemarkPopoverHost — render once, outside the tree
// ---------------------------------------------------------------------------

export const RemarkPopoverHost = () => {
  const { open, remark, anchorPosition } = useRemarkPopoverState();

  if (!remark.length) return null;

  return (
    <Popover
      open={open}
      onClose={() => remarkPopoverStore.close()}
      onClick={(e) => e.stopPropagation()}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: 2,
            maxWidth: 480,
            maxHeight: 400,
            overflow: 'auto',
            boxShadow: (theme: Theme) =>
              `0 8px 32px ${alpha(theme.palette.common.black, 0.18)}`,
          },
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {remark.map((r, index) => (
          <RemarkCard key={index} remark={r} />
        ))}
      </Box>
    </Popover>
  );
};

export const RemarkIndicator = ({ remark }: { remark: RemarkItem[] }) => {
  const dotRef = React.useRef<HTMLSpanElement>(null);
  const isError = remark.some((r) => r.level === 'error');

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (dotRef.current) {
      remarkPopoverStore.open(remark, dotRef.current);
    }
  };

  const tooltipContent = (
    <Box>
      {remark.map((r, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: index < remark.length - 1 ? 0.5 : 0,
          }}
        >
          <LevelIcon level={r.level} size={14} />
          <span>{r.message}</span>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
      <StatusTooltip title={tooltipContent} maxWidth={500}>
        <Box
          component="span"
          ref={dotRef}
          onClick={handleClick}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: isError ? 'error.main' : 'warning.main',
            display: 'inline-block',
            marginLeft: '6px',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
      </StatusTooltip>
    </Box>
  );
};
